"use client";

import { useCallback, useState } from "react";
import {
    TwitterIcon,
    MonitorIcon,
    PlusIcon,
    ZapIcon,
    RefreshCcwIcon,
    ShoppingCartIcon,
    MusicIcon,
} from "lucide-react";

import { useKeywordProfilesContext } from "@/context/keyword-profiles-context";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import KeywordProfileForm from "./keyword-profile-form";
import { cn } from "@/lib/utils";
import Spinner from "./spinner/spinner";

interface IProps { isLoading: boolean; onApply: () => void; }

export default function KeywordProfilesSidebar({ onApply, isLoading }: IProps) {
    const {
        profiles,
        setActiveProfileId,
        deleteProfile,
    } = useKeywordProfilesContext();

    const [isOpen, setIsOpen] = useState(false);
    const [editingProfileId, setEditingProfileId] = useState<string | null>(null);

    const sources = [
        { id: "x", name: "X / Twitter", icon: TwitterIcon, active: true, },
        { id: "youtube", name: "YouTube", icon: MonitorIcon, active: true, },
        { id: "tiktok", name: "TikTok", icon: MusicIcon, active: false },
        { id: "shopee", name: "Shopee", icon: ShoppingCartIcon, active: false },
    ] as const;

    const openCreate = () => {
        setEditingProfileId(null);
        setIsOpen(true);
    };

    const openEdit = (id: string) => {
        setEditingProfileId(id);
        setIsOpen(true);
    };

    const handleOnApply = useCallback(() => {
        onApply();
    }, [onApply]);


    return (
        <div className="space-y-4">
            {/* Sources */}
            <div className="p-4 border border-primary/20 bg-background/40">
                <h2 className="text-sm font-mono tracking-wide text-primary">Supported platforms</h2>

                <div className="mt-3 space-y-2">
                    {sources.map((s) => {
                        const Icon = s.icon;

                        return (
                            <span
                                key={s.id}
                                className={cn("w-full text-left px-3 py-2 flex items-center gap-2 font-mono text-sm", !s.active && 'text-muted-foreground')}
                            >
                                <Icon className="w-4 h-4" />
                                {s.name}
                            </span>
                        );
                    })}
                </div>
            </div>

            {/* Profiles */}
            <div className="p-4 border border-primary/20 bg-background/40">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-mono tracking-wide text-primary">
                        Keyword profiles
                    </h2>
                    {profiles.length ? <Button size={'icon-sm'} variant="outline" onClick={openCreate}>
                        <PlusIcon className="w-4 h-4" />
                    </Button> : null}
                </div>

                <div className="mt-3 space-y-2">
                    {profiles.length ? <>
                        {profiles.map((p) => (
                            <div
                                key={p.id}
                                className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer hover:bg-primary/10 transition`}
                                onClick={() => {
                                    setActiveProfileId(p.id);
                                    openEdit(p.id);
                                }}
                            >
                                <div className="flex flex-col">
                                    <span className="font-mono text-sm">{p.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {p.source.toUpperCase()} • {p.keywords.length} keywords
                                    </span>
                                </div>

                                <button
                                    className="text-xs text-red-400 hover:text-red-500"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteProfile(p.id);
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                        <Button disabled={isLoading} onClick={handleOnApply} variant={'outline'} className="mt-2 w-full">
                            {!isLoading ? <RefreshCcwIcon /> : <Spinner />}
                            <span>Apply Profiles</span>
                        </Button>
                    </> : (
                        <Button variant="outline" className="w-full justify-center" onClick={openCreate}>
                            <PlusIcon className="w-4 h-4 mr-2" />
                            Create Profile
                        </Button>
                    )}
                </div>
            </div>

            {/* SHADCN DIALOG */}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="border border-primary/20">
                    <DialogHeader>
                        <DialogTitle>
                            {/* {editingProfileId ? "Edit Profile" : "Create Profile"} */}
                        </DialogTitle>
                    </DialogHeader>

                    <KeywordProfileForm
                        profileId={editingProfileId}
                        onClose={() => {
                            setIsOpen(false);
                        }}
                    />

                    <DialogClose asChild>
                        <Button variant="destructive">
                            Close
                        </Button>
                    </DialogClose>
                </DialogContent>
            </Dialog>
        </div >
    );
}
